// Surveys 2.0 renderer (spec §6) — one view for every SDK:
//   card  → Intercom-style card (web corner popover); on phones / native apps
//           the bottom-sheet variant (handle, flush bottom)
//   full  → Typeform-style full screen (question number, OK ✓ press Enter ↵,
//           bottom bar with % and up/down)
//   page  → the full layout on a transparent background for the HelpCenterApp
//           survey page and embeds (no close button, height posted to host)
import {
  CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CommunicationManager from "../../Helper/CommunicationManager";
import Communicator from "../../Helper/Communicator";
import HeightUpdateManager from "../../Helper/HeightUpdateManager";
import { longestRemaining } from "../../Helper/surveyLogic";
import { buildSurveyPalette } from "../../Helper/surveyTheme";
import { contactFromSession, TokenContext } from "../../Helper/surveyText";
import { useConfigStore } from "../../Stores/Config";
import { useSessionStore } from "../../Stores/Session";
import { useSurveyV2Store } from "../../Stores/SurveyV2";
import {
  EndingBody,
  QuestionBody,
  SurveyRenderContext,
  WelcomeBody,
} from "./SurveyBlocks";
import { SurveyIcon } from "./SurveyIcons";
import { Stage } from "./SurveyStage";
import { useSurveyStrings } from "./surveyStrings";
import "./SurveyV2.scss";

/** gleap.ai with the same referral tags as the widget's badge, per survey surface. */
const poweredByUrl = (content: string, apiUrl: string, sdkKey: string) => {
  const params = new URLSearchParams({
    utm_source: "widget",
    utm_medium: "referral",
    utm_campaign: "powered_by",
    utm_content: content,
  });
  if (apiUrl) params.set("utm_term", apiUrl);
  if (sdkKey) params.set("ref", sdkKey);
  return `https://www.gleap.ai/?${params.toString()}`;
};

const DEFAULT_THANK_YOU_SECONDS = 4;
/**
 * In-app surveys show nothing (and report no height, the host's cue to reveal
 * a card) until the definition is in, so one with nothing left to ask
 * ("Skip when known") closes unseen. A slower load gets the skeleton after
 * this long: under the JS SDK's own 1.2 s reveal fallback for a card frame
 * (counted from the frame opening, after the messenger's first ping), so the
 * host never reveals an empty frame first.
 */
const LOADING_VIEW_DELAY_MS = 1000;

const prefersReducedMotion = () => {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (exp) {
    return false;
  }
};

const detectKeyboard = () => {
  try {
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  } catch (exp) {
    return true;
  }
};

const prefersDark = () => {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch (exp) {
    return false;
  }
};

/** The visitor's light / dark system setting, kept current (survey page "auto"). */
const useSystemDark = (enabled: boolean) => {
  const [dark, setDark] = useState(prefersDark);
  useEffect(() => {
    if (!enabled) return;
    let query: MediaQueryList | undefined;
    try {
      query = window.matchMedia("(prefers-color-scheme: dark)");
    } catch (exp) {
      return;
    }
    const onChange = () => setDark(query!.matches);
    onChange();
    try {
      query.addEventListener("change", onChange);
      return () => query!.removeEventListener("change", onChange);
    } catch (exp) {
      // Safari < 14
      query.addListener(onChange);
      return () => query!.removeListener(onChange);
    }
  }, [enabled]);
  return dark;
};

const detectPhone = () => {
  try {
    return (
      window.matchMedia("(hover: none) and (pointer: coarse)").matches &&
      window.innerWidth < 640
    );
  } catch (exp) {
    return false;
  }
};

const MAX_STEP_PILLS = 8;
const WELCOME_KEY = "welcome";

/**
 * Step pills (card / sheet header): one pill per step on the expected path —
 * the answered blocks, the current one and the longest remaining path from
 * it — recomputed after every answer, so with branching it's "about N steps".
 * Longer paths keep 8 pills and advance proportionally.
 */
function StepPills({
  answered,
  remaining,
  label,
  percent,
  complete = false,
}: {
  answered: number;
  remaining: number;
  label: string;
  percent: number;
  complete?: boolean;
}) {
  const total = complete
    ? Math.max(1, answered)
    : Math.max(1, answered + Math.max(1, remaining));
  const count = Math.min(total, MAX_STEP_PILLS);
  const current = complete
    ? count
    : total > MAX_STEP_PILLS
      ? // Round up, so the first answer already moves the pills.
        Math.min(count - 1, Math.ceil((answered * count) / total))
      : Math.min(answered, count - 1);
  return (
    <div
      className="sv2-pills"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      aria-label={label}
    >
      {Array.from({ length: count }).map((_, index) => (
        <i
          key={index}
          className={
            index < current
              ? "is-done"
              : index === current
                ? "is-current"
                : undefined
          }
        />
      ))}
    </div>
  );
}

function Skeleton({ full }: { full: boolean }) {
  return (
    <div
      className={`sv2-skeleton ${full ? "sv2-skeleton--full" : ""}`}
      aria-hidden="true"
    >
      {!full && (
        <div className="sv2-skel-row">
          <i className="sv2-skel sv2-skel--ava" />
          <i className="sv2-skel" style={{ width: 110 }} />
        </div>
      )}
      {full && <i className="sv2-skel" style={{ width: 34, height: 12 }} />}
      <i className="sv2-skel sv2-skel--title" style={{ width: "88%" }} />
      <i className="sv2-skel sv2-skel--title" style={{ width: "56%" }} />
      <div className="sv2-skel-controls">
        {Array.from({ length: full ? 11 : 11 }).map((_, i) => (
          <i key={i} className="sv2-skel sv2-skel--box" />
        ))}
      </div>
    </div>
  );
}

function SurveyV2() {
  const phase = useSurveyV2Store((s) => s.phase);
  const format = useSurveyV2Store((s) => s.format);
  const def = useSurveyV2Store((s) => s.def);
  const payload = useSurveyV2Store((s) => s.payload);
  const options = useSurveyV2Store((s) => s.options);
  const path = useSurveyV2Store((s) => s.path);
  const cursor = useSurveyV2Store((s) => s.cursor);
  const endingId = useSurveyV2Store((s) => s.endingId);
  const direction = useSurveyV2Store((s) => s.direction);
  const saveState = useSurveyV2Store((s) => s.saveState);
  const answeredOnce = useSurveyV2Store((s) => s.answeredOnce);
  const answers = useSurveyV2Store((s) => s.answers);
  const lang = useSurveyV2Store((s) => s.lang);
  const progress = useSurveyV2Store((s) => s.progress());
  const close = useSurveyV2Store((s) => s.close);
  const back = useSurveyV2Store((s) => s.back);
  const forward = useSurveyV2Store((s) => s.forward);
  const welcome = useSurveyV2Store((s) => s.welcome);
  const startSurvey = useSurveyV2Store((s) => s.startSurvey);
  const surveyOptions = useSurveyV2Store((s) => s.options);
  const known = useSurveyV2Store((s) => s.known);
  const knownAnswers = useSurveyV2Store((s) => s.knownAnswers);

  const config = useConfigStore((s) => s.config);
  const isApp = useConfigStore((s) => s.isApp);
  const rtl = useConfigStore((s) => s.isRTLLang());
  const session = useSessionStore((s) => s.session);
  const sessionApiUrl = useSessionStore((s) => s.apiUrl);
  const sessionSdkKey = useSessionStore((s) => s.sdkKey);
  const t = useSurveyStrings();

  const rootRef = useRef<HTMLDivElement>(null);

  // In-app (not a survey page or a link inside the messenger): hold the
  // loading view back for LOADING_VIEW_DELAY_MS per load (see above).
  const inApp = format !== "page" && !surveyOptions?.inMessenger;
  const isReady = phase === "ready";
  const [loadingViewDue, setLoadingViewDue] = useState(false);
  useEffect(() => {
    setLoadingViewDue(false);
    if (!inApp || isReady) return;
    const timer = setTimeout(() => setLoadingViewDue(true), LOADING_VIEW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [inApp, isReady, surveyOptions]);
  // Nothing on screen yet: also nothing after an in-app survey closed unseen (phase idle).
  const holdBack = inApp && !isReady && (phase !== "loading" || !loadingViewDue);
  const [hasKeyboard] = useState(detectKeyboard);
  const [phone] = useState(detectPhone);
  const reducedMotion = useMemo(prefersReducedMotion, []);

  const mode = format;
  const sheet =
    mode === "card" && (isApp || !!(options as any)?.sheet || phone);
  const systemDark = useSystemDark(mode === "page");

  // The server sends the theme resolved with the widget defaults; the raw
  // definition theme fills in for older servers.
  const theme = useMemo(
    () => ({ ...(def?.theme ?? {}), ...(payload?.theme ?? {}) }),
    [def?.theme, payload?.theme],
  );
  const palette = useMemo(
    () =>
      buildSurveyPalette(
        theme,
        {
          color: config.color,
          backgroundColor: config.backgroundColor,
        },
        mode === "page" ? { systemDark } : undefined,
      ),
    [theme, config.color, config.backgroundColor, mode, systemDark],
  );

  const tokens: TokenContext = useMemo(
    () => ({
      contact: contactFromSession(session),
      fields: options?.fields ?? {},
      // {{answer.email}} of a question skipped as known shows the contact's value.
      answers: { ...knownAnswers(), ...answers },
      def,
      lang,
      yesLabel: t("svYes"),
      noLabel: t("svNo"),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, options?.fields, answers, known, def, lang],
  );

  const ctx: SurveyRenderContext | null = def
    ? {
        mode,
        sheet,
        def,
        lang,
        t,
        tokens,
        hasKeyboard,
        sender:
          payload?.sender?.name && def?.sender?.show !== false
            ? { ...payload.sender, company: config.name }
            : undefined,
      }
    : null;

  const stepKey = endingId
    ? `end:${endingId}`
    : welcome
      ? WELCOME_KEY
      : (path[cursor] ?? "");
  const currentEnding = endingId
    ? def?.endings.find((e) => e.id === endingId)
    : undefined;

  // ── Corners + surface → host: the JS SDK and appnew.html draw the card /
  // sheet outline, so they round it to the survey's corners and paint the
  // sheet (handle strip included) in the survey's surface colour. Hosts that
  // don't know the message keep their rounded outline in the widget colours.
  const hasDef = !!def;
  const surface = palette.vars["--sv-bg"];
  useEffect(() => {
    if (!hasDef) return;
    try {
      CommunicationManager.getInstance().sendMessage({
        name: "survey-theme",
        data: { corners: palette.corners, surface, dark: palette.dark },
      });
    } catch (exp) {}
  }, [hasDef, palette.corners, surface, palette.dark]);

  // ── Survey page: the frame's colour scheme matches the survey, so the
  // browser keeps the iframe transparent on the host's backdrop (an iframe
  // whose color-scheme differs from its embedder's gets an opaque canvas).
  useEffect(() => {
    if (mode !== "page") return;
    try {
      document.documentElement.style.colorScheme = palette.dark ? "dark" : "light";
    } catch (exp) {}
  }, [mode, palette.dark]);

  // ── Survey page: the frame is as tall as the survey and the host page
  // scrolls, so wheel / trackpad scrolls over the frame must chain out to it.
  // The widget's `overscroll-behavior: none` (index.scss) would swallow them:
  // a long question's OK button stayed below the fold (#185684). ──
  useEffect(() => {
    if (mode !== "page") return;
    try {
      document.documentElement.style.overscrollBehavior = "auto";
      document.body.style.overscrollBehavior = "auto";
    } catch (exp) {}
  }, [mode]);

  // ── Height → host (card + page). Full screen fills its frame. ──
  useEffect(() => {
    if (mode === "full") return;
    const el = rootRef.current;
    if (!el) return;
    const sendHeight = () => HeightUpdateManager.getInstance().sendHeight();
    sendHeight();
    let observer: ResizeObserver | undefined;
    try {
      observer = new ResizeObserver(sendHeight);
      observer.observe(el);
    } catch (exp) {}
    return () => observer?.disconnect();
  }, [mode, phase, holdBack]);

  // ── Survey page: the host page scrolls, so ask it to bring the frame's
  // top back into view when a step changes below a scrolled-past question. ──
  const lastPageStep = useRef(stepKey);
  useEffect(() => {
    if (mode !== "page" || !stepKey) return;
    if (lastPageStep.current === stepKey) return;
    const initial = !lastPageStep.current;
    lastPageStep.current = stepKey;
    if (initial) return;
    try {
      CommunicationManager.getInstance().sendMessage({
        name: "survey-scroll-into-view",
        data: { smooth: !reducedMotion },
      });
    } catch (exp) {}
  }, [stepKey, mode, reducedMotion]);

  // ── Full screen: every step starts at the top (a tall question scrolled
  // down must not hand its scroll position to the next one). ──
  useLayoutEffect(() => {
    if (mode !== "full") return;
    const scroller = rootRef.current?.querySelector<HTMLElement>(".sv2-full");
    if (!scroller || scroller.scrollTop === 0) return;
    try {
      scroller.scrollTo({ top: 0, behavior: reducedMotion ? "auto" : "smooth" });
    } catch (exp) {
      scroller.scrollTop = 0;
    }
  }, [stepKey, mode, reducedMotion]);

  // ── Focus: move to each new question (never on the first card render, so
  // an unprompted card doesn't steal focus from the host page). ──
  const firstStep = useRef(true);
  useEffect(() => {
    if (!stepKey || phase !== "ready") return;
    const initial = firstStep.current;
    firstStep.current = false;
    if (initial && mode !== "full") return;
    const id = setTimeout(() => {
      const step = rootRef.current?.querySelector(".sv2-step--active");
      if (!step) return;
      const input = step.querySelector<HTMLElement>(
        "input[data-autofocus], textarea[data-autofocus]",
      );
      const target =
        input && hasKeyboard
          ? input
          : step.querySelector<HTMLElement>(".sv2-title");
      try {
        target?.focus({ preventScroll: true });
      } catch (exp) {}
    }, 30);
    return () => clearTimeout(id);
  }, [stepKey, phase, mode, hasKeyboard]);

  // ── Ending: auto close (in-app) / auto redirect (page). ──
  useEffect(() => {
    if (!currentEnding) return;
    if (mode === "page" && !(options as any)?.inMessenger) {
      if (currentEnding.redirectUrl) {
        const id = setTimeout(
          () => Communicator.openURL(currentEnding.redirectUrl!, false),
          1500,
        );
        return () => clearTimeout(id);
      }
      return;
    }
    const seconds =
      typeof currentEnding.autoCloseSeconds === "number"
        ? currentEnding.autoCloseSeconds
        : currentEnding.button
          ? 0
          : Math.round(
              (config.thankYouDuration ?? DEFAULT_THANK_YOU_SECONDS * 1000) /
                1000,
            );
    if (seconds > 0) {
      const id = setTimeout(() => close(), seconds * 1000);
      return () => clearTimeout(id);
    }
  }, [currentEnding, mode, close, config.thankYouDuration]);

  // ── Keyboard ──
  const pendingDigit = useRef<{ digit: string; timer: any } | null>(null);
  const onKeyDown = useCallback((event: KeyboardEvent) => {
    if (
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.altKey
    ) {
      return;
    }
    const state = useSurveyV2Store.getState();
    if (state.phase !== "ready") return;
    const target = event.target as HTMLElement;
    const tag = target?.tagName;
    const inText =
      tag === "TEXTAREA" ||
      tag === "SELECT" ||
      (tag === "INPUT" && (target as HTMLInputElement).type !== "file") ||
      target?.isContentEditable;

    if (event.key === "Escape") {
      if (state.format === "card") {
        event.preventDefault();
        state.close();
      }
      return;
    }

    // Welcome screen: Enter starts (the Start button activates natively).
    if (state.welcome) {
      if (
        event.key === "Enter" &&
        !inText &&
        !event.isComposing &&
        tag !== "BUTTON" &&
        tag !== "A"
      ) {
        event.preventDefault();
        state.startSurvey();
      }
      return;
    }

    const block = state.currentBlock();
    if (!block) return;
    const active = rootRef.current?.querySelector(".sv2-step--active");

    if (event.key === "Enter") {
      if (tag === "TEXTAREA" && event.shiftKey) return;
      // Options: Space toggles/selects the focused option; Enter submits
      // once something is picked (Typeform behaviour) and selects an
      // unpicked radio. Other buttons/links activate natively.
      const role = target?.getAttribute?.("role");
      const submitsFromOption =
        role === "checkbox" ||
        (role === "radio" && target.getAttribute("aria-checked") === "true");
      if ((tag === "BUTTON" || tag === "A") && !submitsFromOption) return;
      if (event.isComposing) return;
      event.preventDefault();
      state.submitCurrent();
      return;
    }
    if (inText) return;

    const navigable = state.format !== "card" && !!state.def?.allowSkipBack;
    if (navigable && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
      if (target?.closest?.("[role=radiogroup],[role=group]")) return;
      event.preventDefault();
      if (event.key === "ArrowUp") state.back();
      else state.forward();
      return;
    }

    // Letters pick choices (A, B, C… / Y, N).
    if (/^[a-z]$/i.test(event.key)) {
      const option = active?.querySelector<HTMLElement>(
        `[data-key="${event.key.toUpperCase()}"]`,
      );
      if (option) {
        event.preventDefault();
        option.click();
        option.focus({ preventScroll: true });
      }
      return;
    }

    // Digits pick scale values in full screen / page ("1" then "0" = 10).
    if (/^[0-9]$/.test(event.key) && state.format !== "card") {
      const pick = (digit: string) => {
        const option = active?.querySelector<HTMLElement>(
          `[data-digit="${digit}"]`,
        );
        option?.click();
        option?.focus({ preventScroll: true });
      };
      const hasTen = !!active?.querySelector('[data-digit="10"]');
      if (pendingDigit.current) {
        clearTimeout(pendingDigit.current.timer);
        const combined = pendingDigit.current.digit + event.key;
        pendingDigit.current = null;
        if (combined === "10") {
          event.preventDefault();
          pick("10");
          return;
        }
        pick("1");
      }
      if (event.key === "1" && hasTen) {
        event.preventDefault();
        pendingDigit.current = {
          digit: "1",
          timer: setTimeout(() => {
            pendingDigit.current = null;
            pick("1");
          }, 450),
        };
        return;
      }
      if (active?.querySelector(`[data-digit="${event.key}"]`)) {
        event.preventDefault();
        pick(event.key);
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onKeyDown]);

  const openButton = (url: string) => {
    Communicator.openURL(url, true);
    if (mode !== "page") setTimeout(() => close(), 400);
  };

  const renderStep = (key: string, active: boolean) => {
    if (!ctx || !def) return null;
    if (key === WELCOME_KEY) {
      if (!def.welcome) return null;
      return (
        <WelcomeBody welcome={def.welcome} ctx={ctx} onStart={startSurvey} />
      );
    }
    if (key.startsWith("end:")) {
      const ending = def.endings.find((e) => `end:${e.id}` === key);
      if (!ending) return null;
      return <EndingBody ending={ending} ctx={ctx} onButton={openButton} />;
    }
    const block = def.blocks.find((b) => b.id === key);
    if (!block) return null;
    return <QuestionBody block={block} ctx={ctx} active={active} />;
  };

  const style = palette.vars as CSSProperties;
  const percent = Math.round(progress * 100);
  const showProgress = def ? def.showProgress !== false : true;
  // Paid plans get no "Powered by Gleap" (and no card footer at all). Older
  // servers don't send `branding`; fall back to the widget setting.
  const hideBranding =
    typeof payload?.branding === "boolean"
      ? !payload.branding
      : !!config.hideBranding;
  const openPoweredBy = (medium: string) =>
    Communicator.openURL(
      poweredByUrl(medium, sessionApiUrl, sessionSdkKey),
      true,
    );
  const loading = phase !== "ready" || !ctx;

  const saveIndicator = (compact: boolean) => {
    if (!answeredOnce) return null;
    if (saveState === "retrying") {
      return (
        <span className="sv2-save sv2-save--offline">
          <SurveyIcon name="cloud-off" size={12} />
          {t("svOffline")}
        </span>
      );
    }
    if (saveState === "saving") {
      return (
        <span className="sv2-save sv2-save--saving">
          <SurveyIcon name="loader" size={12} className="sv2-spin" />
          {t("svSaving")}
        </span>
      );
    }
    if (saveState === "saved") {
      return (
        <span className="sv2-save sv2-save--saved">
          <SurveyIcon name="cloud-check" size={12} />
          {compact ? t("svSaved") : t("svAnswerSaved")}
        </span>
      );
    }
    return null;
  };

  const closeButton = (
    <button
      type="button"
      className="sv2-x"
      aria-label={t("svClose")}
      onClick={() => close()}
    >
      <SurveyIcon name="x" size={16} />
    </button>
  );

  const rootClass = [
    "sv2-root",
    `sv2-root--${mode}`,
    sheet ? "sv2-root--sheet" : "",
    palette.dark ? "sv2-root--dark" : "",
    mode !== "card" ? `sv2-root--bg-${palette.background}` : "",
    mode !== "card" && palette.backdropDark ? "sv2-root--on-dark" : "",
    `sv2-root--${palette.corners}`,
    reducedMotion ? "sv2-root--reduced" : "",
    mode !== "full" ? "sv2-measure" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // The same step pills on every surface (card header, full/page bottom bar).
  const remainingSteps =
    def && !loading && !endingId && !welcome
      ? Math.max(1, longestRemaining(def, path[cursor] ?? "", (id) => known[id] !== undefined))
      : 0;
  const stepPills =
    def && !loading && !welcome ? (
      <StepPills
        answered={endingId ? Math.max(1, cursor + 1) : cursor}
        remaining={remainingSteps}
        complete={!!endingId}
        percent={percent}
        label={
          endingId
            ? t("svPercentCompleted", { p: 100 })
            : t("svQuestionOfAbout", {
                n: cursor + 1,
                total: cursor + remainingSteps,
              })
        }
      />
    ) : null;

  // No root, so no .sv2-measure and no height-update while held back.
  if (holdBack) return null;

  if (mode === "card") {
    const status = saveIndicator(false);
    const pillsInHead = showProgress && !endingId && !!stepPills;
    // Progress off: no pills either, so the same floating close.
    const bareHead = !loading && (!!endingId || welcome || !showProgress);
    return (
      <div
        ref={rootRef}
        className={rootClass}
        style={style}
        dir={rtl ? "rtl" : "ltr"}
      >
        <section
          className="sv2-card"
          aria-label={t("svSurvey")}
          aria-busy={loading}
        >
          {/* Space for the host-drawn sheet handle. */}
          {sheet && <div className="sv2-handle" aria-hidden="true" />}
          {/* Header row: step pills on the left, close on the right. Without
              pills (welcome, ending, progress off) the close floats in the
              corner so the content keeps even space above and below. */}
          <div className={bareHead ? "sv2-head sv2-head--bare" : "sv2-head"}>
            {pillsInHead ? stepPills : <span aria-hidden="true" />}
            {closeButton}
          </div>
          <div className={bareHead ? "sv2-body sv2-body--bare" : "sv2-body"}>
            {loading ? (
              <Skeleton full={false} />
            ) : (
              <Stage
                stepKey={stepKey}
                direction={direction}
                renderStep={renderStep}
              />
            )}
          </div>
          {/* The footer carries the branding (free plans) and, on every plan,
              the calm offline state while answers wait to be retried. */}
          {(!hideBranding || saveState === "retrying") && (
            <div className="sv2-foot">
              <span aria-live="polite" className="sv2-foot-status">
                {status}
              </span>
              {!hideBranding && (
                <button
                  type="button"
                  className="sv2-powered-link"
                  onClick={() => openPoweredBy("survey_card")}
                >
                  {t("svPoweredBy")}
                </button>
              )}
            </div>
          )}
        </section>
      </div>
    );
  }

  // Full screen + page.
  const canNavigate =
    !!def?.allowSkipBack && !endingId && !loading && !welcome;
  const canBack = canNavigate && cursor > 0;
  const status = saveIndicator(true);
  return (
    <div
      ref={rootRef}
      className={rootClass}
      style={style}
      dir={rtl ? "rtl" : "ltr"}
    >
      <section
        className="sv2-full"
        aria-label={t("svSurvey")}
        aria-busy={loading}
        // Colour / image backdrop (+ overlay). The page's host paints its own.
        style={
          mode === "full" && palette.backdrop
            ? { background: palette.backdrop }
            : undefined
        }
      >
        {mode === "full" && palette.logoUrl && (
          <img className="sv2-logo" src={palette.logoUrl} alt="" />
        )}
        {mode === "full" && closeButton}
        <div className="sv2-fbody">
          <div className="sv2-fin">
            {loading ? (
              <Skeleton full />
            ) : (
              <Stage
                stepKey={stepKey}
                direction={direction}
                renderStep={renderStep}
              />
            )}
          </div>
        </div>
        {/* Kept on the welcome screen too (empty), so Start doesn't shift the column. */}
        {(showProgress || canNavigate || status || !hideBranding) && (
          <div className="sv2-fbar">
            {showProgress && stepPills}
            <span aria-live="polite">{status}</span>
            {!hideBranding && (
              <button
                type="button"
                className="sv2-powered-link sv2-powered-link--bar"
                onClick={() =>
                  openPoweredBy(mode === "page" ? "survey_page" : "survey_full")
                }
              >
                {t("svPoweredBy")}
              </button>
            )}
            {canNavigate && (
              <div className="sv2-nav">
                <button
                  type="button"
                  aria-label={t("svPrevious")}
                  disabled={!canBack}
                  onClick={() => back()}
                >
                  <SurveyIcon name="chevron-up" size={16} strokeWidth={2.4} />
                </button>
                <button
                  type="button"
                  aria-label={t("svNextQuestion")}
                  onClick={() => forward()}
                >
                  <SurveyIcon name="chevron-down" size={16} strokeWidth={2.4} />
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

export default SurveyV2;
